import clsx from 'clsx'
import {getIn} from 'formik'
import {cloneDeep, compact, isEmpty, isEqual, mapValues, pick} from 'lodash'
import React, {useCallback, useEffect, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {ShowAddress} from '../../../../../_gori/components'
import {KTSVG} from '../../../../../_gori/helpers'
import {useStatesGlobal} from '../../../../../_gori/helpers/components/StatesGlobalProvider'
import {FormError, SelectFormik} from '../../../../../_gori/partials/widgets'
import {useOrdersProvider} from '../../../../../_gori/providers/OrdersProvider'
import {OrdersConfig} from '../../core/_const'
import {FromOrToModal} from '../../modals/FromOrToModal'

type Props = {
  formik: any
  fromToModal: any
  handleCloseFromToModal: any
  disabled?: boolean
  name?: string
  hasReturn?: boolean
  optionReturn?: string
  hasGetAddress?: boolean
  handleOpenFromToModal?: (name: string, data: any) => void
  showSelectedAddressCard?: boolean
}

const EditAddress: React.FC<Props> = ({
  formik,
  fromToModal,
  handleCloseFromToModal,
  disabled = false,
  name = '',
  hasReturn = false,
  optionReturn = '',
  hasGetAddress = true,
  handleOpenFromToModal,
  showSelectedAddressCard = false,
}) => {
  const intl = useIntl()
  const {statesGlobal, getAddress} = useStatesGlobal()
  const address = statesGlobal.address
  const {optionsAddress, setOptionsAddress} = useOrdersProvider()

  const nameReturn = useMemo(() => {
    let _nameReturn = name

    if (hasReturn) {
      _nameReturn = name === 'from' ? 'to' : 'from'
    }

    if (optionReturn) {
      _nameReturn = optionReturn
    }

    return _nameReturn
  }, [hasReturn, name, optionReturn])

  // BEGIN: Field Address
  useEffect(() => {
    if (!hasGetAddress) return

    // Initialize optionsAddress if not already initialized
    setOptionsAddress((prev) => {
      if (!prev[name]) {
        return {...prev, [name]: []}
      }
      return prev
    })

    getAddress(nameReturn)

    return () => {
      // Reset optionsAddress when component unmounts
      setOptionsAddress((prev) => ({...prev, [name]: []}))
    }
  }, [getAddress, hasGetAddress, name, nameReturn, setOptionsAddress])

  const hasMeaningfulAddressData = useCallback((item, keyPrefix = '') => {
    if (!item || typeof item !== 'object') return false
    const displayKeys = ['company', 'first_name', 'last_name', 'street1', 'city', 'state', 'zip']
    return displayKeys.some((k) => {
      const key = keyPrefix ? `${keyPrefix}_${k}` : k
      const v = item[key]
      return v != null && String(v).trim() !== ''
    })
  }, [])

  const getComparableAddressValue = useCallback((item, keyPrefix = '') => {
    if (!item || typeof item !== 'object') return {}

    return Object.values(cloneDeep(OrdersConfig.SHIPPING)).reduce((values, config) => {
      if (
        [OrdersConfig.SHIPPING.VERIFIED.value, OrdersConfig.SHIPPING.SAVE_ADDRESS.value].includes(
          config.value
        )
      ) {
        return values
      }

      const key = keyPrefix ? `${keyPrefix}_${config.value}` : config.value
      values[config.value] =
        item[key] ?? (config.value === OrdersConfig.SHIPPING.RESIDENTIAL.value ? false : '')

      return values
    }, {})
  }, [])

  useEffect(() => {
    if (address === '') {
      return
    }

    const apiOptions = !isEmpty(address?.[nameReturn])
      ? address[nameReturn]
          .filter((item) => item && hasMeaningfulAddressData(item))
          .map((item) => {
            const {first_name, last_name, company, street1, city, state, zip, is_verified} = item
            const fullName = compact([first_name, last_name]).join(', ')
            const labelOption = (
              <div>
                <div className='fw-bolder'>{company}</div>
                <div className='fs-7'>{fullName}</div>
                <div className='d-block'>{compact([street1, city, state, zip]).join(', ')}</div>
                {is_verified && (
                  <div className='text-success'>{intl.formatMessage({id: 'VERIFIED_ADDRESS'})}</div>
                )}
              </div>
            )

            let valueOption = {}
            const customValues = pick(item, [
              ...Object.values(cloneDeep(OrdersConfig.SHIPPING)).map((item) => {
                return item.value
              }),
            ])
            if (customValues) {
              valueOption = Object.entries(customValues).reduce((_values, [key, value]) => {
                switch (key) {
                  case OrdersConfig.SHIPPING.RESIDENTIAL.value:
                    _values[`${name}_${key}`] = value || false
                    break
                  case OrdersConfig.SHIPPING.VERIFIED.value:
                  case OrdersConfig.SHIPPING.SAVE_ADDRESS.value:
                    break
                  default:
                    _values[`${name}_${key}`] = value || ''
                    break
                }
                return _values
              }, {})
            }

            return {
              label: labelOption,
              value: valueOption,
            }
          })
      : []

    // Add current selected address from formik into options (if API doesn't contain it)
    const selectedAddress = formik?.values?.[name]
    const mergedOptions = [...apiOptions]

    if (!isEmpty(selectedAddress)) {
      const selectedOption = mergedOptions.find((option) => isEqual(option?.value, selectedAddress))

      let isCheckData = true
      const allNullExceptResidential = Object.entries(selectedAddress).every(([key, value]) =>
        key === `${name}_is_residential` ? true : value === null || value === ''
      )
      if (allNullExceptResidential) {
        isCheckData = false
      }

      if (!selectedOption && isCheckData && hasMeaningfulAddressData(selectedAddress, name)) {
        const fullName = compact([
          selectedAddress?.[`${name}_first_name`],
          selectedAddress?.[`${name}_last_name`],
        ]).join(', ')
        const labelOption = (
          <div>
            <div className='fw-bolder'>{selectedAddress?.[`${name}_company`]}</div>
            <div className='fs-7'>{fullName}</div>
            <div className='d-block'>
              {compact([
                selectedAddress?.[`${name}_street1`],
                selectedAddress?.[`${name}_city`],
                selectedAddress?.[`${name}_state`],
                selectedAddress?.[`${name}_zip`],
              ]).join(', ')}
            </div>
            {selectedAddress?.[`${name}_is_verified`] && (
              <div className='text-success'>{intl.formatMessage({id: 'VERIFIED_ADDRESS'})}</div>
            )}
          </div>
        )

        mergedOptions.unshift({
          label: labelOption,
          value: selectedAddress,
        })
      }
    }

    const filteredOptions = mergedOptions.filter((opt) =>
      hasMeaningfulAddressData(opt?.value, name)
    )

    setOptionsAddress((prev) => ({...prev, [name]: filteredOptions}))
  }, [address, formik?.values, hasMeaningfulAddressData, intl, name, nameReturn, setOptionsAddress])

  const checkValidateFailed = useMemo(() => {
    const companyOrName =
      !formik.values[name][`${name}_company`] && !formik.values[name][`${name}_first_name`]
    const validate = [
      `${name}_city`,
      `${name}_state`,
      `${name}_country`,
      `${name}_zip`,
      `${name}_street1`,
    ]
    const required = validate.some((item) => !formik.values[name][item])
    const isSubmitAction = formik.submitCount > 0
    const forceInvalidOnSubmit = isSubmitAction && companyOrName && required

    return forceInvalidOnSubmit || companyOrName !== required
  }, [formik.values, name, formik.submitCount])
  // END: Field Address

  useEffect(() => {
    if (checkValidateFailed) {
      formik?.setFieldTouched(name, true)
    }
    // eslint-disable-next-line
  }, [checkValidateFailed, name])

  const handleAddAddress = (nameSave, address) => {
    if (!hasMeaningfulAddressData(address, nameSave)) return

    const first_name = address[`${nameSave}_first_name`]
    const last_name = address[`${nameSave}_last_name`]
    const company = address[`${nameSave}_company`]
    const street1 = address[`${nameSave}_street1`]
    const city = address[`${nameSave}_city`]
    const state = address[`${nameSave}_state`]
    const zip = address[`${nameSave}_zip`]
    const is_verified = address[`${nameSave}_is_verified`]

    const fullName = compact([first_name, last_name]).join(', ')
    const labelOption = (
      <div>
        <div className='fw-bolder'>{company}</div>
        <div className='fs-7'>{fullName}</div>
        <div className='d-block'>{compact([street1, city, state, zip]).join(', ')}</div>
        {is_verified && (
          <div className='text-success'>{intl.formatMessage({id: 'VERIFIED_ADDRESS'})}</div>
        )}
      </div>
    )

    const newOption = {
      label: labelOption,
      value: address,
    }

    setOptionsAddress((prev) => {
      if (!prev[nameSave] || prev[nameSave].length === 0) {
        return {
          ...prev,
          [nameSave]: [newOption],
        }
      }

      // Get current selected address from formik
      const currentSelected = formik.values[nameSave]

      // Helper function to get core identifier (street + city + state + zip)
      const getCoreFields = (addr) => ({
        street1: addr?.[`${nameSave}_street1`],
        city: addr?.[`${nameSave}_city`],
        state: addr?.[`${nameSave}_state`],
        zip: addr?.[`${nameSave}_zip`],
      })

      const coreFieldsNew = getCoreFields(address)
      const coreFieldsCurrent = getCoreFields(currentSelected)

      // Check if address is from state (previously saved/edited)
      // If current selected has same core fields as new address, it means we're editing state data
      if (isEqual(coreFieldsNew, coreFieldsCurrent) && !isEmpty(currentSelected)) {
        // Keep only API options (those that don't match the core fields)
        const apiOptions = prev[nameSave].filter(
          (option) => !isEqual(getCoreFields(option.value), coreFieldsCurrent)
        )

        // Return updated options: new option + filtered API options
        return {
          ...prev,
          [nameSave]: [newOption, ...apiOptions].filter((item) => !isEmpty(item)),
        }
      }

      // If we reach here, it means either:
      // 1. Core fields don't match (new/different address)
      // 2. No currently selected address
      // Check if exact address already exists (for API data)
      const existingIndex = prev[nameSave].findIndex((option) => isEqual(option.value, address))

      if (existingIndex !== -1) {
        // Update existing exact match (from API)
        const updatedOptions = [...prev[nameSave]]
        updatedOptions[existingIndex] = newOption
        return {
          ...prev,
          [nameSave]: updatedOptions,
        }
      } else {
        // Add new option (from API or completely new address)
        return {
          ...prev,
          [nameSave]: [newOption, ...prev[nameSave]].filter((item) => !isEmpty(item)),
        }
      }
    })
  }

  const handleSaveModal = (nameSave, address) => {
    const trimmedDataAddress = mapValues(address, (value) => {
      if (typeof value === 'string') {
        return value.trim()
      }
      return value
    })
    handleAddAddress(nameSave, trimmedDataAddress)
    formik.setFieldValue(nameSave, trimmedDataAddress)
    handleCloseFromToModal()
  }

  const selectedAddress = useMemo(() => {
    return formik.values?.[name]
  }, [formik.values, name])

  const hasSelectedAddress = useMemo(() => {
    return hasMeaningfulAddressData(selectedAddress, name)
  }, [hasMeaningfulAddressData, name, selectedAddress])

  const isSelectedAddressVerified = useMemo(() => {
    if (!hasSelectedAddress) {
      return false
    }

    const matchedAddress = (address?.[nameReturn] || []).find((item) =>
      isEqual(getComparableAddressValue(item), getComparableAddressValue(selectedAddress, name))
    )

    return matchedAddress
      ? !!matchedAddress?.is_verified
      : !!selectedAddress?.[`${name}_is_verified`]
  }, [address, getComparableAddressValue, hasSelectedAddress, name, nameReturn, selectedAddress])

  const handleEditAddress = () => {
    if (disabled || !hasSelectedAddress || !handleOpenFromToModal) {
      return
    }

    handleOpenFromToModal(name, selectedAddress)
  }

  const handleDeleteAddress = async () => {
    if (disabled || !hasSelectedAddress) {
      return
    }

    await formik.setFieldValue(name, {})
    await formik.setFieldTouched(name, true)
  }

  return (
    <>
      {fromToModal.show && nameReturn === fromToModal.name && (
        <FromOrToModal
          data={fromToModal}
          handleSave={handleSaveModal}
          handleClose={handleCloseFromToModal}
          labelModal={fromToModal?.label}
          disabled={disabled}
          hasReturn={hasReturn}
        />
      )}

      <div className=''>
        {showSelectedAddressCard && hasSelectedAddress ? (
          <div
            className={clsx(
              'mt-5 bg-light border border-gray-300 rounded-3 px-5 py-4 position-relative',
              {
                'edit-selected-address-disabled': disabled,
              }
            )}
          >
            <div className='position-absolute top-0 end-0 d-flex align-items-center gap-3 p-4'>
              <div
                className='d-flex align-items-center justify-content-center'
                onClick={handleEditAddress}
              >
                <KTSVG
                  path={
                    disabled
                      ? '/media/gori/orders/edit-note-disabled.svg'
                      : '/media/gori/orders/edit-note.svg'
                  }
                  className='svg-icon-2'
                />
              </div>
              <div
                className='d-flex align-items-center justify-content-center'
                onClick={handleDeleteAddress}
                style={{width: '17px', height: '18px'}}
              >
                <KTSVG
                  path={
                    disabled
                      ? '/media/gori/orders/trash-disabled.svg'
                      : '/media/gori/orders/trash-active.svg'
                  }
                  className='svg-icon-2'
                />
              </div>
            </div>
            <div className='pe-15'>
              <ShowAddress data={selectedAddress} name={name} />
              {isSelectedAddressVerified ? (
                <div className='text-success mt-2'>
                  {intl.formatMessage({id: 'VERIFIED_ADDRESS'})}
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <div>
            <SelectFormik
              className='mt-5'
              options={optionsAddress?.[name] || []}
              name={name}
              formik={formik}
              required
              placeholder={intl.formatMessage({id: 'PLEASE_CHOOSE'})}
              isSearchable={false}
              emptyDefault={false}
              showMessageError={false}
              disabled={disabled}
            />
          </div>
        )}
        {!showSelectedAddressCard &&
          !isEmpty(formik.getFieldProps(name).value) &&
          !(optionsAddress?.[name] || []).find((option) =>
            isEqual(option.value, formik.values?.[name])
          ) && (
            <div className='px-4 mt-2'>
              <ShowAddress data={formik.values[name]} name={name} />
              {formik.getFieldProps(`${name}.${name}_is_verified`).value ? (
                <div className='text-success mt-2'>
                  {intl.formatMessage({id: 'VERIFIED_ADDRESS'})}
                </div>
              ) : null}
            </div>
          )}
        {checkValidateFailed && getIn(formik.touched, name) && (
          <FormError message={intl.formatMessage({id: 'REQUIRED_INFORMATION_MISSING'})} />
        )}
      </div>
    </>
  )
}

export {EditAddress}
